<script setup lang="ts">
// icons
import { faPlusCircle, faMinusCircle } from '@fortawesome/pro-regular-svg-icons';

// utils
import { isEmpty } from 'ramda';
import dayjs from '@/utils/day';

/********************
 * PROPS & EMITS     *
 ********************/
export interface CiReviewProps {
  review: any;
}
const props = withDefaults(defineProps<CiReviewProps>(), {});

/********************
 * COMPOSITIONS      *
 ********************/
const campsiteStore = useCampsiteStore();
const { $gettext } = useGettext();

/********************
 * REFS & VARS       *
 ********************/
const showOriginal = ref<boolean>(false);
const showOriginalAnswer = ref<boolean>(false);
const commentLang = computed(() => {
  if (props.review?.comment_lang_iso2) {
    return props.review.comment_lang_iso2.toLowerCase();
  }
  return null;
});
const displayLang = computed(() => {
  if (props.review?.used_lang) {
    return props.review.used_lang;
  }
  return commentLang.value;
});
const i18nAnsweredFrom = computed(() => {
  return $gettext('Answered from %{campsite}', { campsite: campsiteStore.campsite?.meta?.name }, true);
});

const title = computed(() => (showOriginal.value ? props.review.untranslated.title : props.review.title));
const comment = computed(() => (showOriginal.value ? props.review.untranslated.comment : props.review.comment));
const advantages = computed(() => (showOriginal.value ? props.review.untranslated.advantages : props.review.advantages));
const disadvantages = computed(() => (showOriginal.value ? props.review.untranslated.disadvantages : props.review.disadvantages));
const hasTranslations = computed(() => !isEmpty(props.review.untranslated));
const answerText = computed(() => (showOriginalAnswer.value ? props.review.answer?.untranslated : props.review.answer?.text));
const hasAnswerTranslations = computed(() => !!props.review.answer.untranslated);

/********************
 * FUNCTIONS         *
 ********************/
function getFormattedDate(date, includeTime = false) {
  const format = includeTime ? 'LLL' : 'LL';
  return dayjs(date).format(format);
}
</script>

<template>
  <div class="review mt-6 overflow-hidden">
    <div v-if="advantages || disadvantages" class="grid grid-cols-2 gap-x-[30px]">
      <div v-if="advantages" class="reviews__likes col-span-2 md:col-span-1">
        <div class="rounded-left mt-1 flex items-center bg-white px-4 py-2 shadow-ci">
          <CiAwesomeIcon :icon="faPlusCircle" class="fill-primary" />
          <span class="ml-2 text-primary">{{ $gettext('Advantages') }}</span>
        </div>
        <div v-if="advantages" :lang="displayLang" class="mt-3">
          {{ advantages }}
        </div>
      </div>
      <div v-if="disadvantages" class="reviews__likes col-span-2 md:col-span-1">
        <div class="rounded-left mt-1 flex items-center bg-white px-4 py-2 shadow-ci">
          <CiAwesomeIcon :icon="faMinusCircle" class="fill-danger" />
          <span class="ml-2 text-danger">{{ $gettext('Disadvantages') }}</span>
        </div>
        <div v-if="disadvantages" :lang="displayLang" class="mt-3">
          {{ disadvantages }}
        </div>
      </div>
    </div>
    <div v-if="advantages || disadvantages" class="lg:!hidden">
      <hr class="my-4 h-px border-0 bg-gray-40" />
    </div>
    <div
      v-if="title"
      :lang="displayLang"
      class="mt-4 font-medium"
      data-cy="ci-review--title"
    >
      {{ title }}
    </div>
    <CiReadMore
      v-if="comment"
      :text="comment"
      :language="commentLang"
      :used-language="displayLang"
      class="mb-6"
    />
    <CiToggleTranslation
      v-if="commentLang != $i18n.locale && hasTranslations"
      v-model="showOriginal"
      :language="commentLang"
      class="pb-5 pt-1"
    />
    <!-- answer -->
    <template v-if="review.answer">
      <div
        class="reviews__review-answer relative z-10 my-6 origin-center rounded-2xl bg-primary p-4 text-white content-[''] after:absolute after:left-8 after:top-[-6px] after:-z-1 after:h-[30px] after:w-10 after:rotate-[50deg] after:skew-x-[30deg] after:rounded-[15%_0_0_0_/_25%] after:bg-primary after:content-['']"
      >
        <div class="mb-2">
          <span class="font-bold"> {{ i18nAnsweredFrom }}</span>
          {{ getFormattedDate(props.review.answer.created_at) }}
        </div>
        <div v-if="props.review.answer.text" class="m-0 p-0">
          <CiReadMore
            :text="answerText"
            :language="props.review.answer.language_code"
            :used-language="props.review.answer.used_lang"
            :text-color="'text-white'"
            :icon-color="'fill-white'"
          />
        </div>
        <CiToggleTranslation
          v-if="hasAnswerTranslations && review.answer.language_code != $i18n.locale"
          v-model="showOriginalAnswer"
          :language="review.answer.language_code"
          theme="light"
          class="pt-5 italic"
        />
      </div>
    </template>
  </div>
</template>
